import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { inZone, zonedInputValue } from '../utils/companyDate';
import { useCompanyTimezone } from '../context/AuthContext';

// The Dashboard "Season 2026-27" tile (migration 094,
// services/seasonConfirmations.js). GET /api/season/current gives the season
// the confirmations are about, whether it has been started, the counts by
// answer and how many customers have no row yet.
//
// Before Start: "Set up the 2026-27 season" with the count Start will create.
// After: confirmed, declined and no reply, each a link to the Customers page
// filtered to it. Customers added since Start can be brought in with one click.
// Remind (services/seasonRemind.js) sends the no reply customers the Settings
// season check-in message: the button first shows what it will send (GET
// /api/season/remind), then sends and shows the result. Inside 48 hours of
// the last reminder the confirm says so and offers Send anyway.
// Schedule sends the same Remind later (migration 113, PUT
// /api/season/remind/schedule): a date and time picked in the company's zone,
// which the server checks as it would the send (quiet hours when texts go,
// the 48-hour guard at that time, at most 30 days out). The pending schedule
// shows with who set it and a Cancel; the last scheduled send's outcome, sent
// or refused, shows under it. GET /api/season/current carries both. The
// picker opens prefilled (defaultSendAt) because iOS Safari draws an empty
// datetime-local input as nothing at all; .season-remind-at in index.css
// sizes it.
// Owners and admins only, like the endpoint. Nothing renders while loading,
// on a failed request, or for a company with no one to ask.

export const seasonFilterState = (status) => ({ seasonFilter: status });

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// "Oct 14" in the company's zone (Chicago when none is given).
export const shortDate = (value, timezone) => new Date(value).toLocaleDateString('en-US', inZone(timezone, {
  month: 'short', day: 'numeric',
}));

const SKIP_TEXT = { no_contact: 'no phone or email', no_season_link: 'no link this season' };

export const remindSummary = (r) => {
  const parts = [];
  if (r.texted > 0) parts.push(`text ${plural(r.texted, 'customer', 'customers')}`);
  if (r.emailed > 0) parts.push(`email ${plural(r.emailed, 'customer', 'customers')}`);
  const reasons = Object.entries(r.skippedReasons || {}).map(([k, n]) => `${n} ${SKIP_TEXT[k] || k}`);
  const skipped = r.skipped > 0 ? ` and skip ${r.skipped} (${reasons.join(', ')})` : '';
  return parts.length ? `This will ${parts.join(' and ')}${skipped}.` : `No one can be reached${skipped ? `: ${r.skipped} skipped (${reasons.join(', ')})` : ''}.`;
};

// "Fri, Nov 20 at 8:00 AM" in the company's zone.
export const dayTime = (value, timezone) => {
  const d = new Date(value);
  const day = d.toLocaleDateString('en-US', inZone(timezone, { weekday: 'short', month: 'short', day: 'numeric' }));
  const time = d.toLocaleTimeString('en-US', inZone(timezone, { hour: 'numeric', minute: '2-digit' }));
  return `${day} at ${time}`;
};

const DAY_MS = 24 * 60 * 60 * 1000;

export const remindResult = (r) => [
  `Texted ${r.texted}`, `emailed ${r.emailed}`, `skipped ${r.skipped}`,
  ...(r.failed > 0 ? [`${r.failed} email${r.failed === 1 ? '' : 's'} failed`] : []),
].join(', ') + '.';

export const MISSED_TEXT = 'Not sent: the app was offline at the scheduled time. Schedule it again.';

// The last scheduled send, sent or refused, as one line.
export const scheduledResultText = (r, timezone) => {
  if (r.ok) return `Scheduled reminder sent ${dayTime(r.at, timezone)}: ${remindResult(r)}`;
  if (r.reason === 'missed') return `Scheduled reminder for ${dayTime(r.scheduledFor, timezone)}. ${MISSED_TEXT}`;
  return `Scheduled reminder for ${dayTime(r.scheduledFor, timezone)} was not sent: ${r.error}`;
};

// 'YYYY-MM-DD' plus one calendar day.
const nextDay = (date) => {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
};

// The picker's first value, as 'YYYY-MM-DDTHH:mm' in the zone: 8:00 AM
// tomorrow, or the 48-hour guard's next allowed time (rounded up to the
// minute) when that is later. A guard time between 9:00 PM and 8:00 AM moves
// to the next 8:00 AM when the reminder texts, as the server would refuse it.
export const defaultSendAt = (now, timezone, nextAllowedAt, texts) => {
  const morning = `${nextDay(zonedInputValue(now, timezone).slice(0, 10))}T08:00`;
  if (!nextAllowedAt) return morning;
  const guard = zonedInputValue(Math.ceil(new Date(nextAllowedAt).getTime() / 60000) * 60000, timezone);
  if (guard <= morning) return morning;
  const hour = Number(guard.slice(11, 13));
  if (!texts || (hour >= 8 && hour < 21)) return guard;
  return `${hour < 8 ? guard.slice(0, 10) : nextDay(guard.slice(0, 10))}T08:00`;
};

function SeasonCard() {
  const navigate = useNavigate();
  const timezone = useCompanyTimezone();
  const [state, setState] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const [remind, setRemind] = useState(null); // the plan, while confirming
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState('');
  const [picking, setPicking] = useState(false); // the schedule picker is open
  const [sendAt, setSendAt] = useState('');
  const [suggestAt, setSuggestAt] = useState(null); // a refusal's next allowed time
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => axios.get(`${process.env.REACT_APP_API_URL}/api/season/current`)
    .then((res) => setState(res.data))
    .catch((err) => console.error('Failed to load the season:', err)), []);

  useEffect(() => { load(); }, [load]);

  const handleStart = async () => {
    setStarting(true);
    setError('');
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/season/start`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start the season');
    } finally {
      setStarting(false);
    }
  };

  const openRemind = async () => {
    setError('');
    setResult('');
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/season/remind`);
      setRemind(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load the reminder');
    }
  };

  const sendRemind = async () => {
    setSending(true);
    setError('');
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/season/remind`, { force: Boolean(remind && remind.recent) });
      setRemind(null);
      setResult(remindResult(res.data));
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send the reminder');
    } finally {
      setSending(false);
    }
  };

  const zone = (state && state.schedule && state.schedule.timezone) || timezone;
  const scheduleUrl = `${process.env.REACT_APP_API_URL}/api/season/remind/schedule`;

  // Prefilled from the Remind plan's nextAllowedAt; 8:00 AM tomorrow when
  // the plan cannot be read.
  const openSchedule = async () => {
    setError('');
    setResult('');
    setSuggestAt(null);
    let plan = null;
    try {
      plan = (await axios.get(`${process.env.REACT_APP_API_URL}/api/season/remind`)).data;
    } catch (err) {
      console.error('Failed to load the reminder plan:', err);
    }
    setSendAt(defaultSendAt(Date.now(), zone, plan && plan.nextAllowedAt, Boolean(plan && plan.texted > 0)));
    setPicking(true);
  };

  const saveSchedule = async () => {
    setSaving(true);
    setError('');
    setSuggestAt(null);
    try {
      await axios.put(scheduleUrl, { sendAt });
      setPicking(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to schedule the reminder');
      setSuggestAt(err.response?.data?.nextAllowedAt || null);
    } finally {
      setSaving(false);
    }
  };

  const cancelSchedule = async () => {
    setError('');
    try {
      await axios.delete(scheduleUrl);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel the scheduled reminder');
    }
  };

  if (!state) return null;
  if (!state.started && !(state.missing > 0)) return null;

  const open = (status) => navigate('/customers', { state: seasonFilterState(status) });
  const link = (status, text) => (
    <button
      type="button"
      onClick={() => open(status)}
      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--color-primary)', textDecoration: 'underline', font: 'inherit' }}
    >
      {text}
    </button>
  );

  return (
    <section className="card season-card" aria-labelledby="season-card-title">
      <h2 id="season-card-title" style={{ fontSize: '1.1rem', margin: '0 0 8px' }}>Season {state.key}</h2>
      {!state.started ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span style={{ flex: 1 }}>
            Set up the {state.key} season: track who confirms service, who declines and who has not
            replied. Start adds {plural(state.missing, 'active customer', 'active customers')} as no reply.
          </span>
          <button type="button" className="btn btn-primary btn-sm" onClick={handleStart} disabled={starting}>
            {starting ? 'Starting...' : 'Start'}
          </button>
        </div>
      ) : (
        <>
          <p style={{ margin: 0 }} data-testid="season-counts">
            {link('confirmed', `${state.counts.confirmed} confirmed`)}
            {', '}
            {link('declined', `${state.counts.declined} declined`)}
            {', '}
            {link('pending', `${state.counts.pending} no reply`)}
          </p>
          {state.lastRemindedAt && (
            <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: 'var(--color-text-light)' }} data-testid="season-last-reminded">
              Last reminded {shortDate(state.lastRemindedAt, timezone)}
            </p>
          )}
          {state.counts.pending > 0 && !remind && !picking && (
            <div style={{ marginTop: '8px' }}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={openRemind}>Remind</button>{' '}
              <button type="button" className="btn btn-secondary btn-sm" onClick={openSchedule}>Schedule</button>
            </div>
          )}
          {picking && (
            <div style={{ marginTop: '8px' }} role="group" aria-label="Schedule a reminder">
              <label htmlFor="season-remind-at" style={{ display: 'block', margin: '0 0 4px' }}>
                Send the reminder at (times are {zone})
              </label>
              <input
                id="season-remind-at"
                className="season-remind-at"
                type="datetime-local"
                value={sendAt}
                min={zonedInputValue(Date.now(), zone)}
                max={zonedInputValue(Date.now() + 30 * DAY_MS, zone)}
                onChange={(e) => setSendAt(e.target.value)}
              />
              <button type="button" className="btn btn-primary btn-sm" onClick={saveSchedule} disabled={saving || !sendAt}>
                {saving ? 'Scheduling...' : 'Save schedule'}
              </button>{' '}
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setPicking(false)} disabled={saving}>Close</button>
              {suggestAt && (
                <div style={{ marginTop: '6px' }}>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setSendAt(zonedInputValue(suggestAt, zone))}>
                    Use {dayTime(suggestAt, zone)}
                  </button>
                </div>
              )}
            </div>
          )}
          {state.schedule?.scheduledAt && (
            <p style={{ margin: '8px 0 0' }} data-testid="season-scheduled">
              Scheduled for {dayTime(state.schedule.scheduledAt, zone)}
              {state.schedule.scheduledBy ? ` by ${state.schedule.scheduledBy}` : ''}.{' '}
              <button type="button" className="btn btn-secondary btn-sm" onClick={cancelSchedule}>Cancel</button>
            </p>
          )}
          {state.schedule?.lastScheduledResult && (
            <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: 'var(--color-text-light)' }} data-testid="season-scheduled-result">
              {scheduledResultText(state.schedule.lastScheduledResult, zone)}
            </p>
          )}
          {remind && (
            <div style={{ marginTop: '8px' }} role="group" aria-label="Send a reminder">
              <p style={{ margin: '0 0 6px' }}>{remindSummary(remind)}</p>
              {remind.recent && (
                <p style={{ margin: '0 0 6px', color: 'var(--color-warning, #b45309)' }}>
                  These customers were reminded {shortDate(remind.lastRemindedAt, timezone)}, less than 48 hours ago.
                </p>
              )}
              {remind.quietHours?.quiet && remind.texted > 0 && (
                <p style={{ margin: '0 0 6px', color: 'var(--color-text-light)' }}>
                  Texts are not sent between 9:00 PM and 8:00 AM in your company's time zone. Try again {remind.quietHours.nextAllowedText}.
                </p>
              )}
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={sendRemind}
                disabled={sending || (remind.texted + remind.emailed === 0) || (remind.quietHours?.quiet && remind.texted > 0)}
              >
                {sending ? 'Sending...' : (remind.recent ? 'Send anyway' : 'Send')}
              </button>{' '}
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setRemind(null)} disabled={sending}>Cancel</button>
            </div>
          )}
          {result && <p role="status" style={{ margin: '8px 0 0' }}>{result}</p>}
          {state.missing > 0 && (
            <div style={{ marginTop: '8px', fontSize: '0.85rem', color: 'var(--color-text-light)' }}>
              {plural(state.missing, 'customer is', 'customers are')} not in the season yet.{' '}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleStart}
                disabled={starting}
              >
                {starting ? 'Adding...' : `Add ${state.missing}`}
              </button>
            </div>
          )}
        </>
      )}
      {error && <p className="text-error" role="alert" style={{ margin: '8px 0 0' }}>{error}</p>}
    </section>
  );
}

export default SeasonCard;
